import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowSquareOut,
  Briefcase,
  EnvelopeSimple,
  FilePdf,
  Layout,
  Lightbulb,
  Link as LinkIcon,
  MagicWand,
  PencilSimple,
  ShareNetwork,
  User,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Sparkline } from './components/Sparkline';
import { useAuth } from '@/lib/hooks/useAuth';
import { portfolioApi } from '@/lib/api/portfolio';
import { tenantApi } from '@/lib/api/tenant';
import { analyticsApi } from '@/lib/api/analytics';
import { inquiriesApi } from '@/lib/api/inquiries';
import { queryKeys } from '@/lib/api/queryKeys';
import { publicPortfolioUrl } from '@/lib/config';

const RECENT_SECTIONS: ReadonlyArray<{ title: string; updated: string; icon: React.ReactNode }> = [
  { title: 'Landing hero', updated: 'Updated 2 hours ago', icon: <Layout /> },
  { title: 'Selected works', updated: 'Updated yesterday', icon: <Briefcase /> },
  { title: 'About · Bio', updated: 'Updated Oct 12, 2026', icon: <User /> },
];

const ACTIVITY: ReadonlyArray<{ label: string; time: string; note?: string; active?: boolean }> = [
  { label: 'Draft saved automatically', time: '2 hours ago', active: true },
  {
    label: 'New inquiry received',
    time: 'Yesterday, 4:12 PM',
    note: 'Project estimation for redesign',
  },
  { label: 'Site successfully published', time: 'Oct 12, 10:00 AM' },
  { label: 'Milestone: 10k total views', time: 'Oct 08, 2:30 PM' },
];

export function DashboardPage() {
  const { user } = useAuth();
  const firstName = user?.name.split(' ')[0] ?? 'there';
  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: () => portfolioApi.get(),
    staleTime: 60_000,
  });
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    staleTime: 60_000,
  });
  const analyticsQuery = useQuery({
    queryKey: queryKeys.analyticsOverview('30d'),
    queryFn: () => analyticsApi.overview('30d'),
    staleTime: 60_000,
  });
  const inquiriesQuery = useQuery({
    queryKey: queryKeys.inquiries,
    queryFn: () => inquiriesApi.list({ limit: 50 }),
    staleTime: 60_000,
  });
  const isPublished = Boolean(portfolioQuery.data?.publishedAt);
  const liveUrl = tenantQuery.data?.subdomain
    ? publicPortfolioUrl(tenantQuery.data.subdomain)
    : '';

  const analytics = analyticsQuery.data;
  const viewsSeries = analytics?.series.map((p) => p.views) ?? [];
  const uniquesSeries = analytics?.series.map((p) => p.uniques) ?? [];
  const views = analytics?.totals.views ?? 0;
  const uniques = analytics?.totals.uniques ?? 0;

  const inquiries = inquiriesQuery.data?.items ?? [];
  const inquiriesTotal = inquiries.length;
  const inquiriesUnread = inquiries.filter((i) => !i.readAt).length;

  return (
    <div className="mx-auto max-w-[1100px] space-y-10">
      <section className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="mb-2 font-display text-3xl font-semibold tracking-tight">
            Welcome back, {firstName}
          </h1>
          <p className="font-editorial text-lg italic text-ink-muted">
            {isPublished
              ? 'Your portfolio is live. Keep it fresh.'
              : 'Your portfolio is still in draft. Publish when you\u2019re ready.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isPublished && liveUrl ? (
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-board px-4 py-2 text-sm font-medium text-ink shadow-sm transition-all hover:border-ink/30 hover:bg-rail"
            >
              View live site <ArrowSquareOut size={14} />
            </a>
          ) : null}
          <Link
            to="/app/editor"
            className="rounded-lg border border-ink bg-ink px-4 py-2 text-sm font-medium text-board transition-all hover:bg-ink/90 hover:shadow-float"
          >
            Open editor
          </Link>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-6 lg:col-span-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard
              label="Page views"
              value={views.toLocaleString()}
              sparkline={viewsSeries}
              loading={analyticsQuery.isPending}
              hint="Last 30 days"
            />
            <KpiCard
              label="Visitors"
              value={uniques.toLocaleString()}
              sparkline={uniquesSeries}
              loading={analyticsQuery.isPending}
              hint="Last 30 days"
            />

            <div className="relative flex flex-col justify-between overflow-hidden rounded-2xl border border-line bg-board p-4 shadow-card">
              <div className="relative z-10">
                <span className="text-xs font-semibold tracking-wide text-ink-muted">
                  Inquiries
                </span>
                <div className="mt-2 font-display text-2xl font-semibold">
                  {inquiriesQuery.isPending ? (
                    <span className="text-ink-faint">—</span>
                  ) : (
                    inquiriesTotal.toLocaleString()
                  )}
                </div>
              </div>
              <div className="relative z-10 mt-2 text-[11px] text-ink-muted">
                {inquiriesQuery.isPending
                  ? ' '
                  : inquiriesTotal === 0
                    ? 'No messages yet'
                    : inquiriesUnread > 0
                      ? `${inquiriesUnread} unread`
                      : 'All caught up'}
              </div>
              <EnvelopeSimple
                weight="fill"
                size={80}
                className="absolute -bottom-4 -right-4 z-0 text-canvas opacity-50"
              />
            </div>
          </div>

          <section className="mt-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold tracking-wide">Recent sections</h2>
              <Link
                to="/app/editor"
                className="flex items-center gap-1 text-xs font-medium text-ink-muted transition-colors hover:text-ink"
              >
                View all pages <ArrowRight size={12} />
              </Link>
            </div>
            <div className="overflow-hidden rounded-2xl border border-line bg-board shadow-card">
              {RECENT_SECTIONS.map((s, i) => (
                <div
                  key={s.title}
                  className={`group flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-canvas ${
                    i < RECENT_SECTIONS.length - 1 ? 'border-b border-line' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-rail text-ink-muted transition-colors group-hover:text-ink [&>*]:h-5 [&>*]:w-5">
                      {s.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">{s.title}</h3>
                      <p className="mt-0.5 text-xs text-ink-muted">{s.updated}</p>
                    </div>
                  </div>
                  <button className="flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium opacity-0 transition-all hover:bg-rail group-hover:opacity-100">
                    <PencilSimple size={12} /> Resume
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-4">
          <div className="group relative overflow-hidden rounded-2xl border border-line bg-rail p-5 transition-colors hover:border-ink/30">
            <div className="mb-2 flex items-start justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-board shadow-sm">
                <Lightbulb size={14} className="text-ink" />
              </div>
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-ink-faint">
                Tip
              </span>
            </div>
            <h3 className="mb-1 mt-3 text-sm font-semibold">Optimize your SEO</h3>
            <p className="mb-4 text-xs leading-relaxed text-ink-muted">
              You haven't set a meta description for your home page. This helps with
              search ranking.
            </p>
            <a
              href="#"
              className="border-b border-ink pb-0.5 text-xs font-medium transition-colors hover:border-ink-muted hover:text-ink-muted"
            >
              Go to SEO settings
            </a>
          </div>

          <div className="rounded-2xl border border-line bg-board p-5 shadow-card">
            <h2 className="mb-4 text-sm font-semibold tracking-wide">Quick actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <QuickAction icon={<MagicWand size={18} />} label="Change theme" to="/app/templates" />
              <QuickAction icon={<FilePdf size={18} />} label="Update resume" to="/app/editor" />
              <QuickAction icon={<LinkIcon size={18} />} label="Custom domain" to="/app/domains" />
              <QuickAction
                icon={<ShareNetwork size={18} />}
                label="Share profile"
                to="#"
              />
            </div>
          </div>

          <div className="min-h-[300px] shrink-0 flex-1 rounded-2xl border border-line bg-board p-5 shadow-card">
            <h2 className="mb-6 text-sm font-semibold tracking-wide">Recent activity</h2>
            <div className="relative space-y-6 pl-6 before:absolute before:left-[4px] before:top-2 before:h-[calc(100%-20px)] before:w-px before:bg-line">
              {ACTIVITY.map((a) => (
                <div key={a.label} className="relative z-10 group">
                  <span
                    className={`absolute -left-[22px] mt-1 h-[11px] w-[11px] rounded-full border-[2px] bg-board ${
                      a.active ? 'border-ink' : 'border-line group-hover:border-ink-muted'
                    }`}
                  />
                  <p
                    className={`text-xs font-medium leading-tight ${
                      a.active ? 'text-ink' : 'text-ink-muted'
                    }`}
                  >
                    {a.label}
                  </p>
                  {a.note ? (
                    <p className="mt-0.5 font-editorial text-[11px] italic text-ink">
                      &ldquo;{a.note}&rdquo;
                    </p>
                  ) : null}
                  <p className="mt-1 text-[11px] text-ink-faint">{a.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
}) {
  const cls = "flex flex-col items-start rounded-xl border border-line bg-canvas p-3 text-left transition-all hover:border-ink hover:shadow-card";
  const inner = (
    <>
      <span className="mb-2 [&>*]:h-[18px] [&>*]:w-[18px]">{icon}</span>
      <span className="text-xs font-semibold">{label}</span>
    </>
  );
  if (to === '#') {
    return <button type="button" className={cls}>{inner}</button>;
  }
  return <Link to={to} className={cls}>{inner}</Link>;
}

function KpiCard({
  label,
  value,
  sparkline,
  loading,
  hint,
}: {
  label: string;
  value: string;
  sparkline: ReadonlyArray<number>;
  loading?: boolean;
  hint?: string;
}) {
  const hasData = sparkline.length > 0 && sparkline.some((n) => n > 0);
  return (
    <div className="rounded-2xl border border-line bg-board p-4 shadow-card transition-transform hover:-translate-y-0.5">
      <div className="mb-2 flex items-start justify-between">
        <span className="text-xs font-semibold tracking-wide text-ink-muted">{label}</span>
        {hint ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
            {hint}
          </span>
        ) : null}
      </div>
      <div className="mb-4 font-display text-2xl font-semibold">
        {loading ? <span className="text-ink-faint">—</span> : value}
      </div>
      {hasData ? (
        <Sparkline points={sparkline} />
      ) : (
        <div className="h-8 rounded bg-rail/40" aria-hidden />
      )}
    </div>
  );
}
